import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import {
  getAccessToken,
  clearAllTokens,
  saveAccessToken,
  saveRefreshToken,
} from "../utils/storage.utils";
import { queryClient } from "./query-client";
import { refreshToken } from "../services/auth-refresh.service";
import { completeTokenRefresh, type RefreshQueueItem } from "./auth-refresh-queue";
import { debugError, debugLog } from "@/utils/debug";

// Survives axios mergeConfig on replay. A second 401 on the same request
// must reject rather than refresh again — isRefreshing is cleared in
// finally before the replay settles, so it cannot bound that loop.
type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

function markForAuthRetry(config: RetryableRequestConfig): boolean {
  if (config._retry === true) {
    return false;
  }
  config._retry = true;
  return true;
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: RefreshQueueItem[] = [];
let isSessionInvalidated = false;

// Endpoints that authenticate themselves and must not have the stored
// access token attached or refreshed by the interceptor (the pending-2FA
// token is passed explicitly and the stored token would override it).
const SELF_AUTH_ENDPOINTS = [
  "auth/sign-in",
  "auth/refresh",
  "auth/2mfa/verify",
  "auth/2mfa/verify-backup-code",
];

const isSelfAuthEndpoint = (url: string): boolean => {
  // Strip query string so "auth/sign-in?foo=bar" still matches.
  const path = url.split("?")[0];
  return SELF_AUTH_ENDPOINTS.some(
    // Match exactly OR when the baseURL path prefix prepends a slash.
    // e.g. "auth/sign-in" and "/auth/sign-in" both match; "evil/auth/sign-in" does not.
    (endpoint) => path === endpoint || path === `/${endpoint}`
  );
};

// A 403 on GET /documents/{id} is a legitimate authorization failure handled
// by the RequestAccessDialog UI, not an auth-expiry case. Every other 403
// should still fall through to the standard auth/logout flow.
const DOCUMENT_GET_PATH_RE = /^\/?documents\/[^/?]+(\?.*)?$/;

const isDocumentGetRequest = (method: string | undefined, url: string | undefined): boolean => {
  if (method == null || url == null) return false;
  if (method.toLowerCase() !== "get") return false;
  return DOCUMENT_GET_PATH_RE.test(url);
};

// The backend signals "out of credits" via a 402 with detail containing this
// string. Other 402 reasons (expired card, plan downgrade) must not trigger
// the top-up dialog because topping up won't fix them.
const OUT_OF_BALANCE_DETAIL = "Not enough balance";

const isOutOfBalanceError = (data: unknown): boolean => {
  if (typeof data !== "object" || data == null) return false;
  const detail = (data as { detail?: unknown }).detail;
  return typeof detail === "string" && detail.includes(OUT_OF_BALANCE_DETAIL);
};

// Request interceptor to add authorization header
axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url ?? "";
    if (isSelfAuthEndpoint(url)) {
      debugLog("http", "request", {
        method: config.method ?? "get",
        url,
        auth: "self",
      });
      return config;
    }

    const token = await getAccessToken();

    if (token != null && token !== "") {
      config.headers.set("Authorization", `Bearer ${token}`);
      isSessionInvalidated = false;
    }

    debugLog("http", "request", {
      method: config.method ?? "get",
      url,
      hasToken: token != null && token !== "",
    });

    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

async function invalidateSession(): Promise<void> {
  isSessionInvalidated = true;
  await clearAllTokens();

  toast.error("Session Expired", {
    description: "Your session has expired. Please log in again.",
  });

  queryClient.setQueryData(["user"], null);
  queryClient.removeQueries();
}

function enqueueFailedRequest(originalRequest: InternalAxiosRequestConfig): Promise<unknown> {
  return new Promise<string>((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  })
    .then((token) => {
      originalRequest.headers.set("Authorization", `Bearer ${token}`);
      return axiosInstance(originalRequest);
    })
    .catch((err: unknown) => Promise.reject(err));
}

async function refreshAndRetry(
  originalRequest: InternalAxiosRequestConfig,
  originalError: unknown
): Promise<unknown> {
  isRefreshing = true;
  try {
    const result = await completeTokenRefresh({
      refresh: refreshToken,
      saveAccessToken,
      saveRefreshToken,
      queue: failedQueue,
    });
    if (result.ok) {
      originalRequest.headers.set("Authorization", `Bearer ${result.accessToken}`);
      return axiosInstance(originalRequest);
    }
    await invalidateSession();
    return Promise.reject(originalError);
  } finally {
    isRefreshing = false;
  }
}

async function handleUnauthorized(
  originalRequest: RetryableRequestConfig,
  error: unknown
): Promise<unknown> {
  const url = originalRequest.url ?? "";
  if (isSessionInvalidated) {
    return Promise.reject(error);
  }
  if (!isSelfAuthEndpoint(url)) {
    // Mark before queue or refresh so a replay 401 cannot re-enter.
    if (!markForAuthRetry(originalRequest)) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return enqueueFailedRequest(originalRequest);
    }
    return refreshAndRetry(originalRequest, error);
  }
  // A 401 from the refresh endpoint means the refresh token itself is
  // expired or revoked — log the user out. Other self-auth endpoints
  // (sign-in, 2FA verify) must NOT trigger logout on 401.
  if (url === "auth/refresh" || url.endsWith("/auth/refresh")) {
    await invalidateSession();
  }
  return Promise.reject(error);
}

async function notifyOutOfBalance(): Promise<void> {
  try {
    const { topUpEvents, TOP_UP_REQUIRED } = await import("../utils/events");
    topUpEvents.emit(TOP_UP_REQUIRED);
  } catch {
    // Import failure must not shadow the 402 error propagation.
  }
}

async function handleResponseError(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError<unknown>(error) || error.config == null) {
    debugError("http", "non-axios error", {
      message: error instanceof Error ? error.message : String(error),
    });
    return Promise.reject(error);
  }

  const originalRequest: InternalAxiosRequestConfig = error.config;
  const status = error.response?.status ?? 0;
  const url = originalRequest.url ?? "";
  debugError("http", "response error", {
    method: originalRequest.method ?? "get",
    url,
    status,
    code: error.code ?? null,
    noResponse: error.response == null,
  });

  if (status === 402 && isOutOfBalanceError(error.response?.data)) {
    await notifyOutOfBalance();
  }

  if (status === 403) {
    if (!isDocumentGetRequest(originalRequest.method, url)) {
      toast.error("Permission denied", {
        description: "You don't have permission to perform this action.",
      });
    }
    return Promise.reject(error);
  }

  if (status === 401) {
    return handleUnauthorized(originalRequest, error);
  }

  return Promise.reject(error);
}

axiosInstance.interceptors.response.use((response) => response, handleResponseError);

export default axiosInstance;
